import { Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

import { AppSidebar } from "@/components/layout/app-sidebar";
import { CommandPalette } from "@/components/layout/command-palette";
import { SpotlightSearchBar } from "@/components/layout/spotlight-search-bar";
import { OperatorStatusStrip } from "@/components/operator/operator-status-strip";
import {
  SidebarInset,
  SidebarProvider,
} from "@/components/ui/sidebar";
import AdminSourcesPage from "@/pages/admin-sources";
import ChatsPage from "@/pages/chats";
import InboxPage from "@/pages/inbox";
import OperatorProcessorPage from "@/pages/operator-processor";
import OperatorPublishPage from "@/pages/operator-publish";
import OperatorSchedulesPage from "@/pages/operator-schedules";
import OperatorScraperPage from "@/pages/operator-scraper";
import OperatorStatusPage from "@/pages/operator-status";
import OperatorWebhooksPage from "@/pages/operator-webhooks";
import SignalCandidatesPage from "@/pages/signal-candidates";
import SourcesPage from "@/pages/sources";
import ToolsPage from "@/pages/tools";
import TradeIdeasDeliveryPage from "@/pages/tradeideas/delivery";
import TradeIdeasFiltersPage from "@/pages/tradeideas/filters";
import TradeIdeasScannersPage from "@/pages/tradeideas/scanners";
import TradeIdeasStatusPage from "@/pages/tradeideas/status";
import VendorPagesAdminPage from "@/pages/vendor-pages-admin";

/**
 * Root layout. App-wide collapsible sidebar on the left, main content
 * on the right. Navigation, collapse, install, and operator actions live
 * in the sidebar so product pages can use the full vertical surface.
 */
export default function App() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border bg-bg/95 px-4">
          <SpotlightSearchBar />
          <OperatorStatusStrip />
          <div
            id="signals-topbar-actions"
            className="ml-auto flex items-center gap-2"
          />
        </header>
        <main className="flex-1 overflow-auto">
          <Routes>
            <Route path="/" element={<Navigate to="/inbox" replace />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/candidates" element={<SignalCandidatesPage />} />
            <Route path="/tools" element={<ToolsPage />} />
            <Route path="/tools/:toolId" element={<ToolsPage />} />
            <Route path="/chats" element={<ChatsPage />} />
            <Route path="/chats/:vendorId" element={<ChatsPage />} />
            <Route path="/tradeideas" element={<Navigate to="/tradeideas/status" replace />} />
            <Route path="/tradeideas/status" element={<TradeIdeasStatusPage />} />
            <Route path="/tradeideas/scanners" element={<TradeIdeasScannersPage />} />
            <Route path="/tradeideas/filters" element={<TradeIdeasFiltersPage />} />
            <Route path="/tradeideas/delivery" element={<TradeIdeasDeliveryPage />} />
            <Route path="/sources" element={<SourcesPage />} />
            <Route path="/admin/sources" element={<AdminSourcesPage />} />
            <Route path="/admin/vendor-pages" element={<VendorPagesAdminPage />} />
            <Route path="/operator/status" element={<OperatorStatusPage />} />
            <Route path="/operator/webhooks" element={<OperatorWebhooksPage />} />
            <Route path="/operator/scraper" element={<OperatorScraperPage />} />
            <Route path="/operator/processor" element={<OperatorProcessorPage />} />
            <Route path="/operator/publish" element={<OperatorPublishPage />} />
            <Route path="/operator/schedules" element={<OperatorSchedulesPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </SidebarInset>
      <CommandPalette />
      <Toaster theme="dark" position="top-right" richColors />
    </SidebarProvider>
  );
}

function NotFound() {
  return (
    <div className="flex h-full items-center justify-center text-muted-fg">
      Not found.
    </div>
  );
}
